import React, { useState } from 'react';
import { Button, Icon } from '@blueprintjs/core';
import { ContentIsActiveProvider } from './ContentIsActiveContext';
import './FeatureCard.css';

/**
 * The `FeatureCard` component represents an individual feature card with optional collapsible functionality.
 * It is a child element of `CollapsibleSection` for drag-and-drop functionality.
 * 
 * @param {object} props - The properties object.
 * @param {string} props.title - The title of the feature card.
 * @param {boolean} props.collapsible - Determines if the card can be collapsed or expanded.
 * @param {React.ReactNode} props.children - The content to be displayed inside the card when not collapsed.
 * 
 * @returns {JSX.Element} The rendered `FeatureCard` component.
 * 
 * @example
 * <FeatureCard title="My Feature" collapsible={true} orientation="horizontal">
 *     <p>This is the content of the card.</p>
 * </FeatureCard>
 */
const FeatureCard = ({ title, collapsible, children, dragHandleProps }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const toggleCollapse = () => {
        setIsCollapsed(prevState => !prevState);
    };

    return (
        <ContentIsActiveProvider>
            <div className="feature-card" style={{ flex: 1 }}>
                <div
                    className="card-header"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    {...dragHandleProps} // Only the header is used as a drag area
                >
                    <span style={{ display: "flex", alignItems: "center" }}>
                        <Icon icon="drag-handle-vertical" className="header-icon" />
                        {title}
                    </span>
                    {collapsible && (
                        <Button
                            className="collapse-button"
                            minimal
                            icon={isCollapsed ? 'chevron-down' : 'chevron-up'}
                            onClick={toggleCollapse}
                        />
                    )}
                </div>
                {!isCollapsed && (
                    <div className="card-content" style={{ flex: 1 }}>
                        {children}
                    </div>
                )}
            </div>
        </ContentIsActiveProvider>
    );
};

export default FeatureCard;

